import { AddressCreateParams, ContactCreateParams } from '@brenger/api-client';
import { useDebounce, useForm } from '@brenger/react';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { AddressFieldsProps } from '.';
import { Button, Col, InputText, Row } from '../../../../../brenger-shared-ui';
import bookBlue from '../../../../../brenger-shared-ui/assets/images/user-group_blue.svg';
import bookWhite from '../../../../../brenger-shared-ui/assets/images/user-group_white.svg';
import { AddressBook } from '../../../../../brenger-shared-ui/components/form/AddressBook';
import { SheetModal } from '../../../../../brenger-shared-ui/components/layout/sheetlayout';
import { useTypedSelector } from '../../../../../hooks';
import { CacheKey } from '../../../../../types';
import { parseLine1Line2 } from '../../../../../utils/address';
import { autocompleteAddress } from '../../../../../utils/geo';
import { translate } from '../../../../../utils/localization';
import { normalizeDutchPostalCode } from '../../../../../utils/normalisation';
import { validateDutchPostalCode, validateRequired, warnLine1 } from '../../../../../utils/validation';
import { isUserLoggedIn } from '../../../../User/ducks';
import { actions, getStopDestinationDetails } from '../../../ducks';

export const AddressFieldsDutch: React.FC<AddressFieldsProps> = ({
  forceShowErrors,
  type,
  setLoading,
  setIsFormError,
}) => {
  const existing = useTypedSelector(state => state.generalTransport.contact[type]);
  const wAddress = useTypedSelector(state => getStopDestinationDetails(state, type));
  const widgetAddress = wAddress ? parseLine1Line2(wAddress as AddressCreateParams, true) : wAddress;
  const dispatch = useDispatch();
  const showAddressBook = useTypedSelector(isUserLoggedIn);
  const [showModal, setShowModal] = React.useState(false);

  const form = useForm({
    persist: {
      key: `gf_contact_address_nl_${type}`,
      type: 'session',
    },
    initialState: {
      isPrefilled: existing?.address_is_prefilled || false,
      postal_code: existing?.address?.postal_code || widgetAddress?.postal_code || '',
      street_number: existing?.address?.line2 || widgetAddress?.line2 || '',
      street: existing?.address?.line1 || widgetAddress?.line1 || '',
      locality: existing?.address?.locality || widgetAddress?.locality || '',
    },
    validators: {
      postal_code: validateDutchPostalCode,
      street_number: val => validateRequired(translate('form.fields.address.house_number.label'), val),
      street: val => validateRequired(translate('form.fields.address.street.label'), val),
    },
  });

  const line2 = form.data.street_number.value;
  const postalCode = form.data.postal_code.value;
  const autoCompleteInput = `${postalCode}${line2}`;
  const debouncedAutocompleteInput = useDebounce(autoCompleteInput, 2500);
  const isTyping = !form.data.isPrefilled.value && autoCompleteInput !== debouncedAutocompleteInput;

  const autocomplete = useQuery({
    queryKey: [CacheKey.RETRIEVE_GEO_LOCATION, debouncedAutocompleteInput],
    queryFn: () =>
      autocompleteAddress({
        line2,
        postal_code: postalCode,
        locality: form.data.locality.value,
        country_code: 'NL',
      }),
    enabled:
      !!debouncedAutocompleteInput &&
      !form.getError('postal_code') &&
      !form.getError('street_number') &&
      !form.data.isPrefilled.value,
  });

  React.useEffect(() => {
    if (autocomplete.data) {
      form.set({
        street: autocomplete.data.line1,
        locality: autocomplete.data.locality,
      });
      const { latitude, longitude, ...rest } = autocomplete.data;
      dispatch(
        actions.setStopAddress({
          stopType: type,
          address: {
            ...rest,
            lat: latitude,
            lng: longitude,
            line2: form.data.street_number.value,
          } as AddressCreateParams,
        })
      );
    } else if (!form.data.isPrefilled.value) {
      dispatch(actions.setStopAddress({ stopType: type, address: null }));
    }
  }, [autocomplete.data]);

  /**
   * Sync loading state to parent
   */
  React.useEffect(() => {
    setLoading(autocomplete.isFetching || isTyping);
  }, [autocomplete.isFetching, isTyping]);

  /**
   * Sync error state to parent
   */
  React.useEffect(() => {
    setIsFormError(form.hasErrors);
  }, [form.hasErrors]);

  const onHistoricalChange = (a: AddressCreateParams): void => {
    const address = parseLine1Line2(a, true);
    form.set({
      isPrefilled: true,
      street: address.line1,
      street_number: address.line2,
      postal_code: address.postal_code,
      locality: address.locality,
    });
    dispatch(
      actions.setStopAddress({
        stopType: type,
        address,
      })
    );
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          {showAddressBook && (
            <Button
              buttonStyle="outline"
              fullWidth={true}
              type="button"
              extraClasses="address-book--contact-button"
              onClick={() => setShowModal(true)}
            >
              <span className="flex justify-between items-center w-full">
                <Translate id="address_book.list_action" />
                <div style={{ width: '20px', height: '20px' }}>
                  <img className="address-book--contact-button-icon-blue" src={bookBlue} />
                  <img className="address-book--contact-button-icon-white" src={bookWhite} />
                </div>
              </span>
            </Button>
          )}
        </Col>
        <Col xs={8} extraClasses="pb-[1em]">
          <InputText
            value={form.data.postal_code.value}
            onChange={postal_code => {
              form.set({ isPrefilled: false, postal_code: normalizeDutchPostalCode(postal_code) });
            }}
            id={`${type}_postal_code`}
            placeholder={translate('form.fields.address.postal_code.label')}
            meta={{
              error: form.getError('postal_code'),
              showError: forceShowErrors || form.data.postal_code.isDirty,
            }}
          />
        </Col>
        <Col xs={4} extraClasses="pb-[1em]">
          <InputText
            value={form.data.street_number.value}
            onChange={street_number => form.set({ isPrefilled: false, street_number })}
            id={`${type}_house_number`}
            placeholder={translate('form.fields.address.house_number.label')}
            meta={{
              error: form.getError('street_number'),
              showError: forceShowErrors || form.data.street_number.isDirty,
            }}
          />
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            value={form.data.street.value}
            onChange={s => form.set({ isPrefilled: false, street: s })}
            id={`${type}_street`}
            disabled={isTyping || autocomplete.isFetching}
            placeholder={translate('form.fields.address.street.label')}
            meta={{
              error: form.getError('street'),
              warning: warnLine1(form.data.street.value),
              showError: forceShowErrors || form.data.street.isDirty,
            }}
          />
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            value={form.data.locality.value}
            id={`${type}_locality`}
            disabled={true}
            placeholder={translate('form.fields.address.locality.label')}
          />
        </Col>
      </Row>
      <SheetModal isActive={showModal} closeHandler={() => setShowModal(false)}>
        <AddressBook
          handleClose={() => setShowModal(false)}
          onSelect={addr => {
            setShowModal(false);
            const { latitude, longitude, ...rest } = addr.address;
            onHistoricalChange({
              ...rest,
              lat: latitude,
              lng: longitude,
            } as AddressCreateParams);
            if (addr.contact)
              dispatch(
                actions.setContactDetails({
                  contact_type: type,
                  details: addr.contact as ContactCreateParams,
                })
              );
            if (addr.instructions)
              dispatch(
                actions.setStopAddress({
                  stopType: type,
                  instructions: addr.instructions,
                })
              );
          }}
        />
      </SheetModal>
    </>
  );
};
